'use client'

import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import type {
    TooltipProps as RadixTooltipProps,
    TooltipContentProps,
} from '@radix-ui/react-tooltip'
import {
    TOOLTIP_ARROW_CLASS,
    TOOLTIP_CONTENT_CLASS,
    TOOLTIP_PORTAL_CONTAINER_CLASS,
} from '@trustoo/components/ui/tooltip/styles'
import { useTooltipPortalContainer } from '@trustoo/components/ui/tooltip/useTooltipPortalContainer'
import TopLayerPanel from '@trustoo/components/ui/TopLayerPanel'
import type { ComponentPropsWithoutRef, ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'

export type TooltipProps = {
    children: ReactNode
    childrenContent: ReactNode
    arrowClassName?: string
} & RadixTooltipProps &
    TooltipContentProps &
    ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>

/**
 * Hover tooltip. The panel renders in the browser top layer next to its trigger, so it is never
 * clipped or covered by a modal; see `useTooltipPortalContainer` for how and why.
 */
export function Tooltip({
    children,
    childrenContent,
    open,
    defaultOpen,
    onOpenChange,
    arrowClassName,
    ...contentProps
}: TooltipProps) {
    const { isOpen, handleOpenChange, container, setContainer } = useTooltipPortalContainer({
        open,
        defaultOpen,
        onOpenChange,
    })

    return (
        <TooltipPrimitive.Root open={isOpen} onOpenChange={handleOpenChange}>
            <TooltipPrimitive.Trigger asChild className="cursor-help">
                {children}
            </TooltipPrimitive.Trigger>
            {isOpen && (
                <TopLayerPanel ref={setContainer} className={TOOLTIP_PORTAL_CONTAINER_CLASS} />
            )}
            {container && (
                <TooltipPrimitive.Portal container={container}>
                    <TooltipPrimitive.Content
                        collisionPadding={16}
                        {...contentProps}
                        className={twMerge(
                            TOOLTIP_CONTENT_CLASS,
                            'max-w-[min(320px,var(--radix-tooltip-content-available-width))]',
                            contentProps.className,
                        )}>
                        {childrenContent}
                        <TooltipPrimitive.Arrow
                            className={twMerge(TOOLTIP_ARROW_CLASS, arrowClassName)}
                        />
                    </TooltipPrimitive.Content>
                </TooltipPrimitive.Portal>
            )}
        </TooltipPrimitive.Root>
    )
}
